/* ============================================================
   Tomi Björck — Live page controller

   1. Resolves the page state (pre / live / ended) from the
      scheduled start/end time, and re-checks it every 15s.
   2. Lets Sprii override the state: if Sprii reports a live show
      in the hidden status gallery, the page flips to "live".
   3. Opens the Sprii player from our own poster / CTAs, using the
      documented window.Sprii.openShow({ eventId }) API, with a
      fallback that clicks the Sprii gallery card, then Sprii's own
      floating widget (the multi-event player resolves the live/next
      show itself), and a final fallback that deep-links to
      ?sprii_event=<id>.
   4. Before the show starts, the primary CTA opens a small
      add-to-calendar chooser: Google and Outlook open a pre-filled
      event in one click, .ics covers Apple and everything else.
      All three carry a 30-minute reminder.

   The chooser's CSS is injected from ensureStyles() so the whole
   widget lives in one file.

   Docs: https://docs.sprii.io/integration/open-shows/
         https://docs.sprii.io/integration/galleries/
   ============================================================ */
(function () {
  'use strict';

  if (window.tbLive) return; // asset may be included by more than one section

  // The shop is Finland-only and all page copy states Helsinki time, so the
  // summary line in the chooser is always rendered in Europe/Helsinki. The
  // calendar entries themselves use absolute UTC timestamps, so they land
  // correctly no matter where the visitor is.
  var DISPLAY_TZ = 'Europe/Helsinki';

  var root = document.documentElement;
  var config = {
    mode: 'auto',
    startAt: null,
    endAt: null,
    eventId: '',
    preAction: 'player',
    calTitle: '',
    calDesc: '',
    calLocation: ''
  };
  var forcedLiveBySprii = false;
  var lastTrigger = null;

  function parseTime(value) {
    if (!value) return null;
    var t = Date.parse(value);
    return isNaN(t) ? null : t;
  }

  function computeState() {
    if (config.mode && config.mode !== 'auto') return config.mode;
    if (forcedLiveBySprii) return 'live';
    var now = Date.now();
    if (config.startAt && now < config.startAt) return 'pre';
    if (config.endAt && now >= config.endAt) return 'ended';
    if (config.startAt && now >= config.startAt) return 'live';
    return 'pre';
  }

  function currentState() {
    return root.getAttribute('data-tb-live-state') || computeState();
  }

  function applyState() {
    var next = computeState();
    if (root.getAttribute('data-tb-live-state') === next) return;
    root.setAttribute('data-tb-live-state', next);
    document.dispatchEvent(new CustomEvent('tb-live:state', { detail: { state: next } }));
  }

  /* ---------- Calendar ---------- */

  function pad(n) {
    return n < 10 ? '0' + n : '' + n;
  }

  function utcStamp(d) {
    return (
      d.getUTCFullYear() +
      pad(d.getUTCMonth() + 1) +
      pad(d.getUTCDate()) +
      'T' +
      pad(d.getUTCHours()) +
      pad(d.getUTCMinutes()) +
      '00Z'
    );
  }

  function icsEscape(text) {
    return String(text || '')
      .replace(/\\/g, '\\\\')
      .replace(/;/g, '\\;')
      .replace(/,/g, '\\,')
      .replace(/\r?\n/g, '\\n');
  }

  function pageUrl() {
    return window.location.origin + window.location.pathname;
  }

  function eventData() {
    if (!config.startAt) return null;
    var start = new Date(config.startAt);
    var end = config.endAt ? new Date(config.endAt) : new Date(config.startAt + 2 * 60 * 60 * 1000);
    var titleEl = document.querySelector('.tb-live-hero__h1');
    var title = (config.calTitle || (titleEl && titleEl.textContent) || document.title || '').trim();
    var url = pageUrl();
    var details = (config.calDesc ? config.calDesc + ' ' : '') + url;
    return {
      start: start,
      end: end,
      title: title,
      details: details,
      location: config.calLocation || url,
      url: url
    };
  }

  function googleUrl(e) {
    return (
      'https://calendar.google.com/calendar/render?action=TEMPLATE' +
      '&text=' + encodeURIComponent(e.title) +
      '&dates=' + utcStamp(e.start) + '/' + utcStamp(e.end) +
      '&details=' + encodeURIComponent(e.details) +
      '&location=' + encodeURIComponent(e.location)
    );
  }

  function outlookUrl(e, host) {
    return (
      'https://' + host + '/calendar/0/deeplink/compose?path=%2Fcalendar%2Faction%2Fcompose&rru=addevent' +
      '&startdt=' + encodeURIComponent(e.start.toISOString()) +
      '&enddt=' + encodeURIComponent(e.end.toISOString()) +
      '&subject=' + encodeURIComponent(e.title) +
      '&body=' + encodeURIComponent(e.details) +
      '&location=' + encodeURIComponent(e.location)
    );
  }

  function icsBody(e) {
    return [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Tomi Bjorck//Live//FI',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      'UID:tb-live-' + config.startAt + '@tomibjorck.fi',
      'DTSTAMP:' + utcStamp(new Date()),
      'DTSTART:' + utcStamp(e.start),
      'DTEND:' + utcStamp(e.end),
      'SUMMARY:' + icsEscape(e.title),
      'DESCRIPTION:' + icsEscape(e.details),
      'LOCATION:' + icsEscape(e.location),
      'URL:' + e.url,
      'BEGIN:VALARM',
      'TRIGGER:-PT30M',
      'ACTION:DISPLAY',
      'DESCRIPTION:' + icsEscape(e.title),
      'END:VALARM',
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\r\n');
  }

  function downloadIcs() {
    var e = eventData();
    if (!e) return false;
    var body = icsBody(e);
    try {
      var blob = new Blob([body], { type: 'text/calendar;charset=utf-8' });
      var href = URL.createObjectURL(blob);
      var a = document.createElement('a');
      a.href = href;
      a.download = 'tomi-bjorck-live.ics';
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      setTimeout(function () {
        URL.revokeObjectURL(href);
        if (a.parentNode) a.parentNode.removeChild(a);
      }, 1500);
      return true;
    } catch (err) {
      window.location.href = 'data:text/calendar;charset=utf-8,' + encodeURIComponent(body);
      return true;
    }
  }

  /* ---------- Calendar chooser dialog ---------- */

  function ensureStyles() {
    if (document.getElementById('tb-live-cal-styles')) return;
    var css =
      '.tb-cal__backdrop{position:fixed;inset:0;z-index:2147483000;display:flex;align-items:center;justify-content:center;padding:20px;background:rgba(20,17,13,.55);backdrop-filter:blur(4px)}' +
      '.tb-cal__box{background:#faf8f4;color:#1b1813;border-radius:14px;max-width:380px;width:100%;padding:22px;box-shadow:0 30px 70px -20px rgba(20,17,13,.5);font-family:var(--font-body-family,inherit)}' +
      '.tb-cal__head{display:flex;align-items:flex-start;justify-content:space-between;gap:12px;margin-bottom:4px}' +
      '.tb-cal__title{margin:0;font-family:var(--font-heading-family,inherit);font-size:20px;font-weight:500;line-height:1.2;color:inherit}' +
      '.tb-cal__when{margin:0 0 16px;font-size:13px;color:#71695e}' +
      '.tb-cal__close{flex:none;width:30px;height:30px;border:0;border-radius:50%;background:rgba(27,24,19,.06);color:#1b1813;font-size:18px;line-height:1;cursor:pointer;font-family:inherit}' +
      '.tb-cal__close:hover{background:rgba(27,24,19,.12)}' +
      '.tb-cal__list{display:flex;flex-direction:column;gap:8px}' +
      '.tb-cal__opt{display:flex;align-items:center;gap:10px;padding:13px 14px;border:1px solid rgba(27,24,19,.14);border-radius:10px;background:#fff;color:#1b1813;font-size:14.5px;font-weight:600;text-decoration:none;cursor:pointer;font-family:inherit;text-align:left;width:100%}' +
      '.tb-cal__opt:hover{border-color:#1b1813}' +
      '.tb-cal__opt:focus-visible{outline:2px solid #96491f;outline-offset:2px}' +
      '.tb-cal__note{margin:14px 0 0;font-size:11.5px;color:#a39b8f}';
    var style = document.createElement('style');
    style.id = 'tb-live-cal-styles';
    style.textContent = css;
    document.head.appendChild(style);
  }

  function formatWhen(e) {
    try {
      var from = new Intl.DateTimeFormat('fi-FI', {
        timeZone: DISPLAY_TZ,
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        hour: '2-digit',
        minute: '2-digit'
      }).format(e.start);
      var to = new Intl.DateTimeFormat('fi-FI', {
        timeZone: DISPLAY_TZ,
        hour: '2-digit',
        minute: '2-digit'
      }).format(e.end);
      return from + '–' + to;
    } catch (err) {
      return '';
    }
  }

  function onChooserKey(ev) {
    if (ev.key === 'Escape') closeChooser();
  }

  function closeChooser() {
    var el = document.querySelector('.tb-cal__backdrop');
    if (!el) return;
    if (el.parentNode) el.parentNode.removeChild(el);
    document.removeEventListener('keydown', onChooserKey);
    if (lastTrigger && lastTrigger.focus) lastTrigger.focus();
  }

  function openCalendarChooser() {
    var e = eventData();
    if (!e) return false;
    closeChooser();
    ensureStyles();

    var backdrop = document.createElement('div');
    backdrop.className = 'tb-cal__backdrop';

    var box = document.createElement('div');
    box.className = 'tb-cal__box';
    box.setAttribute('role', 'dialog');
    box.setAttribute('aria-modal', 'true');
    box.setAttribute('aria-label', e.title);

    var head = document.createElement('div');
    head.className = 'tb-cal__head';
    var title = document.createElement('h2');
    title.className = 'tb-cal__title';
    title.textContent = 'Lisää muistutus kalenteriin';
    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'tb-cal__close';
    close.setAttribute('aria-label', 'Sulje');
    close.innerHTML = '&times;';
    close.addEventListener('click', closeChooser);
    head.appendChild(title);
    head.appendChild(close);

    var when = document.createElement('p');
    when.className = 'tb-cal__when';
    when.textContent = formatWhen(e);

    var list = document.createElement('div');
    list.className = 'tb-cal__list';

    function addLink(label, href) {
      var a = document.createElement('a');
      a.className = 'tb-cal__opt';
      a.href = href;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.textContent = label;
      a.addEventListener('click', function () {
        setTimeout(closeChooser, 150);
      });
      list.appendChild(a);
      return a;
    }

    var first = addLink('Google Kalenteri', googleUrl(e));
    addLink('Outlook', outlookUrl(e, 'outlook.live.com'));
    addLink('Microsoft 365', outlookUrl(e, 'outlook.office.com'));

    var icsBtn = document.createElement('button');
    icsBtn.type = 'button';
    icsBtn.className = 'tb-cal__opt';
    icsBtn.textContent = 'Apple Kalenteri tai muu (.ics)';
    icsBtn.addEventListener('click', function () {
      downloadIcs();
      setTimeout(closeChooser, 150);
    });
    list.appendChild(icsBtn);

    var note = document.createElement('p');
    note.className = 'tb-cal__note';
    note.textContent = 'Muistutus 30 minuuttia ennen lähetystä.';

    box.appendChild(head);
    box.appendChild(when);
    box.appendChild(list);
    box.appendChild(note);
    backdrop.appendChild(box);

    backdrop.addEventListener('click', function (ev) {
      if (ev.target === backdrop) closeChooser();
    });
    document.addEventListener('keydown', onChooserKey);

    document.body.appendChild(backdrop);
    if (first && first.focus) first.focus();
    return true;
  }

  /* ---------- Sprii ---------- */

  function spriiReady() {
    return !!(window.Sprii && typeof window.Sprii.openShow === 'function');
  }

  function findSpriiCard() {
    // Priority order matters, so each selector is tried separately (a single
    // comma-group querySelector would return the DOCUMENT-ORDER first match —
    // e.g. the widget container, whose click listener actually sits on its
    // child). Gallery cards first (a live-show card is the best target); then
    // the clickable expanded popup inside Sprii's floating widget; then the
    // widget's first child. Clicking the widget opens the multi-event player,
    // which resolves the live/upcoming show itself — verified 2026-08-26.
    var selectors = [
      '.sprii-shows-gallery [data-sprii-action="open-show"]',
      '.elisa-shows-gallery [data-sprii-action="open-show"]',
      '.sprii-group.sprii-cursor-pointer',
      '.sprii-group',
      '#elisa-floating-container .popup-expanded',
      '#elisa-floating-container > *'
    ];
    for (var i = 0; i < selectors.length; i++) {
      var el = document.querySelector(selectors[i]);
      if (el) return el;
    }
    return null;
  }

  function clickSpriiCard() {
    var card = findSpriiCard();
    if (!card) return false;
    card.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
    return true;
  }

  /**
   * Primary CTA action. Before the show starts this opens the calendar
   * chooser; otherwise it opens the Sprii player (retrying ~6s while the
   * Sprii widget boots).
   */
  function openShow(trigger) {
    lastTrigger = trigger || null;

    if (config.preAction === 'calendar' && currentState() === 'pre') {
      return openCalendarChooser();
    }

    var eventId =
      (trigger && trigger.getAttribute && trigger.getAttribute('data-sprii-event-id')) || config.eventId || '';

    function attempt() {
      if (eventId && spriiReady()) {
        window.Sprii.openShow({ eventId: eventId });
        return true;
      }
      return clickSpriiCard();
    }

    if (attempt()) return true;

    var tries = 0;
    var timer = setInterval(function () {
      tries += 1;
      if (attempt() || tries >= 30) {
        clearInterval(timer);
        if (tries >= 30) {
          if (eventId) {
            // Documented deep-link fallback.
            var url = new URL(window.location.href);
            url.searchParams.set('sprii_event', eventId);
            window.location.href = url.toString();
          } else if (window.console) {
            console.warn('[tb-live] Sprii player not available and no event ID configured.');
          }
        }
      }
    }, 200);
    return false;
  }

  function bindTriggers(scope) {
    var nodes = (scope || document).querySelectorAll('[data-tb-live-open]');
    Array.prototype.forEach.call(nodes, function (el) {
      if (el.dataset.tbLiveBound === '1') return;
      el.dataset.tbLiveBound = '1';
      el.addEventListener('click', function (event) {
        event.preventDefault();
        openShow(el);
      });
    });
  }

  /**
   * Watch the hidden data-sprii-statuses="live" gallery. When Sprii injects
   * a card into it, a show is actually live right now.
   */
  function watchSpriiLive() {
    var probe = document.querySelector('[data-tb-live-probe]');
    if (!probe || probe.dataset.tbWatched === '1') return;
    probe.dataset.tbWatched = '1';

    function evaluate() {
      var hasCard = !!probe.querySelector('[data-sprii-action="open-show"], .sprii-group');
      if (hasCard && !forcedLiveBySprii) {
        forcedLiveBySprii = true;
        applyState();
      }
    }

    probe.addEventListener('sprii-cards-added', evaluate);
    probe.addEventListener('sprii-data-loaded', evaluate);
    new MutationObserver(evaluate).observe(probe, { childList: true, subtree: true });
    evaluate();
  }

  /* ---------- Boot ---------- */

  function readConfig() {
    var host = document.querySelector('[data-tb-live-config]');
    if (!host) return false;
    config.mode = host.getAttribute('data-state-mode') || 'auto';
    config.startAt = parseTime(host.getAttribute('data-start-at'));
    config.endAt = parseTime(host.getAttribute('data-end-at'));
    config.eventId = host.getAttribute('data-sprii-event-id') || '';
    config.preAction = host.getAttribute('data-pre-action') || 'player';
    config.calTitle = host.getAttribute('data-cal-title') || '';
    config.calDesc = host.getAttribute('data-cal-description') || '';
    config.calLocation = host.getAttribute('data-cal-location') || '';
    return true;
  }

  function init() {
    var hasConfig = readConfig();
    // Theme-editor / QA override: ?tb_state=pre|live|ended
    var override = new URLSearchParams(window.location.search).get('tb_state');
    if (override && ['pre', 'live', 'ended'].indexOf(override) !== -1) config.mode = override;

    if (hasConfig || override) {
      applyState();
      setInterval(applyState, 15000);
      watchSpriiLive();
    } else if (!root.getAttribute('data-tb-live-state')) {
      root.setAttribute('data-tb-live-state', 'pre');
    }
    bindTriggers();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-bind after theme-editor section reloads.
  document.addEventListener('shopify:section:load', function (event) {
    readConfig();
    applyState();
    bindTriggers(event.target);
    watchSpriiLive();
  });

  window.tbLive = {
    open: openShow,
    calendar: openCalendarChooser,
    ics: downloadIcs,
    apply: applyState,
    config: config
  };
})();
